<script lang="ts">
  import type {
    IAutomatedProductListNode,
    IProductGridListNode,
    IProductXScrollListNode,
    PartialNode,
  } from "@shared/common/node/type";
  import type { IProductData } from "@shared/common/types/data-source";
  import {
    renderProductItemPrice,
    renderTemplate,
  } from "@shared/common/utils/string-template-helper";
  import { getCurrentDocumentNode, getStore } from "../../context";

  const { reactionEventListener: dispatchReactionEvent, origin } = getStore();

  const documentNode = getCurrentDocumentNode();

  export let product: IProductData;
  export let node:
    | PartialNode<IProductGridListNode>
    | PartialNode<IProductXScrollListNode>
    | PartialNode<IAutomatedProductListNode>;

  $: productUrl = product.url
    ? product.url.startsWith("/")
      ? $origin + product.url // url이 PATHNAME 형식일 경우
      : product.url
    : node.urlFormat
    ? renderTemplate({ template: node.urlFormat, data: product })
    : $origin + "/product/detail.html?product_no=" + product.original_id;

  async function onLinkClick(e: MouseEvent) {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
      return;
    }

    e.preventDefault();

    const element = e.target as HTMLElement;

    dispatchReactionEvent({
      documentNode,
      element,
      node,
      data: {
        action: {
          type: "URL",
          url: productUrl,
          target: "_self",
        },
        nodeId: node?.id || "",
        trigger: {
          type: "ON_CLICK",
        },
        nodeName: `product_${product.original_id}_${product.title}`,
      },
      // eslint-disable-next-line no-console
    }).catch((e) => console.error(e));
  }

  $: showAlt =
    product.price === undefined &&
    product.discount_price === undefined &&
    product.alt !== undefined;
  $: finalPrice = product.discount_price ?? product.price ?? 0;
  $: isRowDirection = node.productCardDirection === "ROW";
</script>

<div class="product-card" class:row={isRowDirection}>
  <div class="image-container" class:row={isRowDirection}>
    <img
      class="product-image"
      src={product.image ?? ""}
      alt={`${product.title ?? "상품"} 이미지`}
    />
  </div>

  <div class="text-container" class:row={isRowDirection}>
    <h3 class="product-title">
      <a href={productUrl} on:click={onLinkClick} target="_self">
        {product.title}
        <div class="product-card-click-area"></div>
      </a>
    </h3>

    {#if product.price && product.discount_price && product.price !== product.discount_price}
      <div class="original-price">
        {renderProductItemPrice({
          currency: product.currency,
          price: product.price,
          format: node.priceFormat,
        })}
      </div>
    {/if}
    <div class="price-container">
      {#if product.price && product.discount_price && product.price !== product.discount_price}
        <span class="discount-rate">
          {((1 - product.discount_price / product.price) * 100).toFixed(0)}%
        </span>
      {/if}
      <span class="final-price">
        {#if showAlt}
          <span style="font-weight: normal">
            {product.alt}
          </span>
        {:else}
          {renderProductItemPrice({
            currency: product.currency,
            price: finalPrice,
            format: node.priceFormat,
          })}
        {/if}
      </span>
    </div>
  </div>
</div>

<style>
  .product-card {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    width: var(--cb-product-item-fixed-width);
    flex: none;
    text-align: left;
    position: relative;
    transition: opacity 0.15s ease;
    overflow: hidden;
    background-color: var(--cb-product-card-background-color);
    border-radius: var(--cb-product-card-border-radius);
    padding: var(--cb-product-card-padding);

    &.row {
      flex-direction: row;
      height: 100%;
    }
  }

  .product-card-click-area {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: 1;
    cursor: pointer;
  }

  .product-card:has(a:hover) {
    opacity: 0.9;
  }

  .image-container {
    border-radius: var(--cb-product-image-border-radius);
    overflow: hidden;
    aspect-ratio: var(--cb-product-image-aspect-ratio);

    &.row {
      height: 100%;
      flex-shrink: 0;
    }
  }

  .product-image {
    object-fit: var(--cb-product-image-object-fit);
    width: 100%;
    height: 100%;
  }

  .product-title {
    overflow: hidden;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
    font-family: var(--cb-product-title-font-family);
    font-size: var(--cb-product-title-font-size);
    font-weight: var(--cb-product-title-font-weight);
    color: var(--cb-product-title-color);
    text-align: var(--cb-product-text-container-text-align);
    line-height: 1.575;
  }

  .text-container {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    padding: var(--cb-product-text-container-padding);
    align-items: var(--cb-product-text-container-align-items);
    background-color: var(--cb-product-text-container-background-color);
    justify-content: flex-start;

    &.row {
      justify-content: space-between;
      height: 100%;
    }
  }

  .product-title a {
    color: inherit;
    text-decoration: inherit;
  }

  .product-title a:hover {
    text-decoration: underline;
  }

  .original-price {
    font-family: var(--cb-product-original-price-font-family);
    font-size: var(--cb-product-original-price-font-size);
    font-weight: var(--cb-product-original-price-font-weight);
    color: var(--cb-product-original-price-color);
    margin-top: var(--cb-product-original-price-margin-top);
    line-height: 1.5;
    text-decoration: line-through;
    word-break: break-all;
  }

  .price-container {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    margin-top: var(--cb-product-price-container-margin-top);
    gap: var(--cb-product-price-container-gap);
  }

  .row .price-container {
    flex-direction: column;
    align-items: var(--cb-product-text-container-align-items);
  }

  .discount-rate {
    font-family: var(--cb-product-discount-ratio-font-family);
    font-size: var(--cb-product-discount-ratio-font-size);
    font-weight: var(--cb-product-discount-ratio-font-weight);
    line-height: 1.285;
    color: var(--cb-product-discount-ratio-color);
  }

  .final-price {
    font-family: var(--cb-product-final-price-font-family);
    font-size: var(--cb-product-final-price-font-size);
    font-weight: var(--cb-product-final-price-font-weight);
    line-height: 1.285;
    color: var(--cb-product-final-price-color);
    word-break: break-all;
  }
</style>
